const TOKEN_KEY = "autotrader.token";

/**
 * One key, no session table — matches the server's stateless 30-day JWT (§21). A PWA that
 * logs you out every time the phone kills the tab is a PWA you stop using; the tradeoff is
 * accepted explicitly in the plan, mitigated by a strict same-origin CSP.
 */
export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

/**
 * Seconds of remaining life a token needs before it counts as usable. **W4.2.**
 *
 * A token with a few seconds left is one the websocket would outlive: it would connect,
 * expire mid-session, and land in the reconnect ladder anyway. Refusing it early just moves
 * the same discovery to before an action instead of during one.
 */
const EXPIRY_LEEWAY_S = 30;

/**
 * Whether a stored token is worth sending. **W4.2.**
 *
 * Nothing here verifies the signature — the client has no secret and the server is the
 * authority on validity. Reading `exp` is not a security check; it is the difference between
 * "I might be logged in" and "I know this cannot possibly work", and the second is what
 * stops the websocket 401-reconnect-looping every 30 seconds forever on day 31.
 *
 * **An unreadable token is treated as usable.** Answering a parsing failure with "you are
 * logged out" would turn a client-side bug into a lockout; sending it and letting the real
 * 401 decide costs one request and keeps the server the authority.
 */
export function tokenIsUsable(token: string): boolean {
  const expiry = expiryOf(token);
  if (expiry === null) return true;
  return expiry - EXPIRY_LEEWAY_S > Date.now() / 1000;
}

/** `exp` in seconds, or null when the token has none or cannot be read. */
function expiryOf(token: string): number | null {
  const payload = token.split(".")[1];
  if (!payload) return null;
  try {
    const json = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    const claims = JSON.parse(json) as { exp?: unknown };
    return typeof claims.exp === "number" ? claims.exp : null;
  } catch {
    return null;
  }
}

/**
 * A pure read, deliberately: it does not clear an expired token. `api.ts`'s 401 handler owns
 * clearing and routing, and a second path doing the same thing is how two mechanisms end up
 * disagreeing about who is logged in.
 */
export function isLoggedIn(): boolean {
  const token = getToken();
  return token !== null && tokenIsUsable(token);
}
